import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import SydAutocomplete from '../../../../commonDesign/SydAutocomplete'
import { useListViewTags } from '../../../../../api/abundanceEngine'
import useDebouncedValue from '../../../../../hooks/useDebounceValue'

/**
 * Per-view tag picker for the AE view create/edit form.
 *
 * Same component / styling as the `Filter by tag` autocomplete on the AE list
 * page (`ViewTagsAutocomplete.jsx`), with two adaptations for form use:
 *   - it's controlled (caller passes `value: number[]` of viewTagIds)
 *   - it always loads the full tag list (no filter is selected) so the
 *     selected tags hydrate from saved view data
 *
 * Selected-tag metadata is held in a local cache keyed by `viewTagId` so the
 * chips don't disappear when the user types in the search box (the filtered
 * `useListViewTags` response would otherwise drop tags not matching the
 * search). The cache is seeded from the initial query data and updated as
 * the user adds/removes options.
 */
function ViewTagsPicker ({ value = [], onChange, placeholder = 'Select tags...' }) {
  const [searchText, setSearchText] = useState('')
  const debouncedText = useDebouncedValue(searchText, 200)

  const { data, isLoading } = useListViewTags({
    sort: [{ field: 'name', dir: 'asc' }],
    limit: 100,
    filters: debouncedText
      ? { search: [{ op: 'contains', value: debouncedText }] }
      : undefined
  })

  const allTags = useMemo(() => data || [], [data])

  const options = useMemo(
    () => allTags.map((tag) => ({
      label: tag.displayName || tag.name,
      subLabel: tag.description,
      value: tag.viewTagId
    })),
    [allTags]
  )

  // Stable cache of selected-tag metadata so chips survive search-driven
  // refetches. We deliberately only cache ids that are currently in `value`
  // (i.e. actually selected on this view) — the upper bound is the number of
  // tags a single view carries, not the firm-wide tag universe. If a value id
  // shows up in `allTags` (initial unfiltered load, or a search result that
  // happens to include it), we capture it; if it doesn't show up we keep the
  // last seen copy. Entries are evicted when the user deselects an id.
  const selectedCacheRef = useRef(new Map())
  useEffect(() => {
    const desiredIds = new Set(Array.isArray(value) ? value : [])
    // Capture metadata for any currently-selected id present in this allTags slice.
    for (const t of allTags) {
      if (t?.viewTagId != null && desiredIds.has(t.viewTagId)) {
        selectedCacheRef.current.set(t.viewTagId, t)
      }
    }
    // Evict entries for ids that are no longer selected.
    for (const cachedId of [...selectedCacheRef.current.keys()]) {
      if (!desiredIds.has(cachedId)) selectedCacheRef.current.delete(cachedId)
    }
  }, [allTags, value])

  // Map the form's viewTagIds[] -> [{ label, subLabel, value }] for SydAutocomplete.
  // Read from the local cache (not `allTags`) so the chips render their display
  // labels regardless of the current search filter. If a selected id has never
  // been seen (rare — id was set programmatically before any list response),
  // fall back to a minimal label so the chip still renders and is removable.
  const selectedOptions = useMemo(() => {
    const ids = Array.isArray(value) ? value : []
    return ids.map((id) => {
      const tag = selectedCacheRef.current.get(id)
      if (tag) {
        return { label: tag.displayName || tag.name, subLabel: tag.description, value: tag.viewTagId }
      }
      return { label: `Tag #${id}`, value: id }
    })
  }, [value])

  return (
    <SydAutocomplete
      options={options}
      value={selectedOptions}
      onChange={(_e, selected) => {
        // Capture metadata for any newly-selected option from the current
        // (possibly search-filtered) response. The effect above handles the
        // bulk case; this covers the click-then-blur-search race.
        for (const opt of (selected || [])) {
          if (opt?.value != null && !selectedCacheRef.current.has(opt.value)) {
            const tag = allTags.find((t) => t.viewTagId === opt.value)
            if (tag) selectedCacheRef.current.set(opt.value, tag)
          }
        }
        const ids = (selected || []).map((opt) => opt.value).filter((v) => v != null)
        onChange?.(ids)
      }}
      onInputChange={(_e, text) => setSearchText(text)}
      placeholder={placeholder}
      size='sm'
      isLoading={isLoading}
      multiple
    />
  )
}

ViewTagsPicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  placeholder: PropTypes.string
}

export default ViewTagsPicker
